import { put, select, takeLatest } from 'redux-saga/effects'
import { VitalsConstants } from '../store/vitals.constants'
import { setComments, saveVitals } from '../store/vitals.actions'
import { getPatientVitalsSelector } from '../selectors/patientVitals.selector'
import { getVitalOptionsSelector } from '../../options/selectors'
import { IPainQuality, IPatientVitals } from '../store/vitals.types'
import { IVitalOptionsState } from '../../options/store/options.types'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'

const PAIN_QUALITY_PREFIX = 'Pain Quality:'
const FIELD_KEYS = ['field1', 'field2', 'field3', 'field4', 'field5', 'field6', 'field7'] as const

function extractExistingPQBlock(comments: string): string {
  const prefixIndex = comments.indexOf(PAIN_QUALITY_PREFIX)
  if (prefixIndex < 0) return ''

  const pqSection = comments.slice(prefixIndex)
  const match = pqSection.match(/^Pain Quality:(?:\s*[^;]+-[^;]*;)*/)
  return match ? match[0] : ''
}

function buildPainQualityCommentBlock(
  painQuality: IPainQuality | null,
  fieldOptions?: IVitalOptionsState['painQualityFieldOptions']
): string {
  if (!painQuality) return ''

  const onset = painQuality.onset || ''
  const onsetUnitName = painQuality.onsetUnit?.name?.trim() ?? ''
  const comments = (painQuality.comments ?? '').trim()

  const segments: string[] = []

  if ((onset !== null && onset !== '') || onsetUnitName) {
    segments.push(`Onset - ${onset} ${onsetUnitName};`)
  }

  FIELD_KEYS.forEach((key) => {
    const label = fieldOptions?.[key]?.label?.trim()
    const value = painQuality[key]?.name?.trim()

    if (label && value) {
      segments.push(`${label} - ${value};`)
    }
  })

  if (!segments.length && !comments) return ''

  let result = PAIN_QUALITY_PREFIX

  if (segments.length) {
    result += ` ${segments.join(' ')}`
  }

  if (comments) {
    const commentsSegment = ` Comments - ${comments};`
    result += commentsSegment
  }

  return result
}

function extractAfterPQBlock(comments: string, prefixIndex: number): string {
  const pqSection = comments.slice(prefixIndex)
  const match = pqSection.match(/^Pain Quality:(?:\s*[^;]+-[^;]*;)*/)
  if (match) {
    return comments.slice(prefixIndex + match[0].length)
  }
  return ''
}

function upsertPainQualityCommentBlock(currentComments: string, painQualityBlock: string): string {
  const prefixIndex = currentComments.indexOf(PAIN_QUALITY_PREFIX)

  let after = ''
  if (prefixIndex >= 0) {
    const oldBlock = extractExistingPQBlock(currentComments)
    if (oldBlock) {
      const oldBlockIndex = currentComments.indexOf(oldBlock, prefixIndex)
      if (oldBlockIndex >= 0) {
        after = currentComments.slice(oldBlockIndex + oldBlock.length)
      } else {
        after = extractAfterPQBlock(currentComments, prefixIndex)
      }
    } else {
      after = extractAfterPQBlock(currentComments, prefixIndex)
    }
  }

  if (!painQualityBlock) {
    if (prefixIndex < 0) return currentComments
    const before = currentComments.slice(0, prefixIndex).trimEnd()
    const cleaned = before.endsWith(';') ? before.slice(0, -1).trimEnd() : before
    return after.trim() ? `${cleaned} ${after.trimStart()}`.trim() : cleaned
  }

  const before = prefixIndex < 0 ? currentComments.trimEnd() : currentComments.slice(0, prefixIndex).trimEnd()
  if (!before.trim()) return `${painQualityBlock}${after}`

  return `${before} ${painQualityBlock}${after}`
}

export function* generateAndSavePainQualityComments() {
  const vitals: IPatientVitals = yield select(getPatientVitalsSelector)
  const vitalOptions: IVitalOptionsState = yield select(getVitalOptionsSelector)

  const painQualityBlock = buildPainQualityCommentBlock(vitals.painQuality, vitalOptions.painQualityFieldOptions)
  const updatedComments = upsertPainQualityCommentBlock(vitals.comments, painQualityBlock)

  if (updatedComments !== vitals.comments) {
    yield put(setComments(updatedComments))
  }
  yield put(saveVitals())
}

export function* generateAndSavePainQualityCommentsSaga() {
  yield takeLatest(
    [
      VitalsConstants.SET_PAIN_QUALITY,
      VitalsConstants.SET_PAIN_QUALITY_ONSET,
      VitalsConstants.SET_PAIN_QUALITY_ONSET_UNIT,
      VitalsConstants.SET_PAIN_QUALITY_FIELD1,
      VitalsConstants.SET_PAIN_QUALITY_FIELD2,
      VitalsConstants.SET_PAIN_QUALITY_FIELD3,
      VitalsConstants.SET_PAIN_QUALITY_FIELD4,
      VitalsConstants.SET_PAIN_QUALITY_FIELD5,
      VitalsConstants.SET_PAIN_QUALITY_FIELD6,
      VitalsConstants.SET_PAIN_QUALITY_FIELD7,
      VitalsConstants.SET_PAIN_QUALITY_COMMENTS,
    ],
    performWithMutexHOG,
    generateAndSavePainQualityComments
  )
}
